import type { ApiLiquidStakingState, ApiNft, ApiStakingState } from '../../../api/types';
import type { getActions } from '../../index';
import type { AccountChain, GlobalState } from '../../types';
import { AppState } from '../../types';

import {
  DEFAULT_STAKING_STATE,
  IS_GRAM_WALLET,
  MW_CARDS_COLLECTION,
  STAKING_SLUG_PREFIX,
  SWAP_API_VERSION,
  TELEGRAM_GIFTS_SUPER_COLLECTION,
} from '../../../config';
import { parseAccountId } from '../../../util/account';
import { areDeepEqual } from '../../../util/areDeepEqual';
import { buildCollectionByKey, omitUndefined, unique } from '../../../util/iteratees';
import { openUrl } from '../../../util/openUrl';
import { normalizeAllowedOnOffRampCurrencies } from '../../../util/rampCurrencies';
import { getIsActiveStakingState } from '../../../util/staking';
import { persistCache } from '../../cache';
import { omitAccounts } from '../../helpers/auth';
import { pinMwCardsFirst } from '../../helpers/nfts';
import { addActionHandler, getGlobal, setGlobal } from '../../index';
import {
  addUnorderedNfts,
  applyIncomingNftFromActivity,
  applyOutgoingNftFromActivity,
  createAccount,
  removeNft,
  replaceNft,
  updateAccount,
  updateAccountBanners,
  updateAccountChain,
  updateAccountOwnedMwCards,
  updateAccountSettings,
  updateAccountSettingsBackgroundNft,
  updateAccountStaking,
  updateAccountState,
  updateBalances,
  updateCurrencyRates,
  updateCurrentAccountId,
  updateNft,
  updateRestrictions,
  updateSettings,
  updateStakingDefault,
  updateSwapTokens,
  updateTokens,
  updateVesting,
  updateVestingInfo,
} from '../../reducers';
import {
  selectAccount,
  selectAccountNftByAddress,
  selectAccountSettings,
  selectAccountState,
  selectVestingPartsReadyToUnfreeze,
} from '../../selectors';

// Accumulates new My Wallet Cards across multi-batch streaming rounds.
// Drained on the round's final batch when `streamedAddresses` is present.
const pendingNewMwCardsByAccount = new Map<string, ApiNft[]>();
let pendingLegacySignIn: { accountId: string; address: string; auth: GlobalState['auth'] } | undefined;

addActionHandler('apiUpdate', (global, actions, update) => {
  switch (update.type) {
    case 'updateBalances': {
      global = updateBalances(global, update.accountId, update.chain, update.balances);
      setGlobal(global);
      actions.preloadTokenDetails();
      break;
    }

    case 'updateStaking': {
      const {
        accountId,
        states,
        totalProfit,
        shouldUseNominators,
      } = update;

      const stateById = buildCollectionByKey(states, 'id');

      global = updateStakingDefault(global, {
        ...stateById[DEFAULT_STAKING_STATE.id] as ApiLiquidStakingState,
        balance: 0n,
        unstakeRequestAmount: 0n,
        tokenBalance: 0n,
      });
      const prevStakingStateById = selectAccountState(global, accountId)?.staking?.stateById || {};
      const prevStakingIds = new Set(Object.keys(prevStakingStateById));

      global = updateAccountStaking(global, accountId, {
        stateById,
        shouldUseNominators,
        totalProfit,
      });

      const { stakingId } = selectAccountState(global, accountId)?.staking ?? {};

      if (!stakingId) {
        let stateWithBiggestBalance: ApiStakingState | undefined;

        if (states.length > 0) {
          stateWithBiggestBalance = states.reduce((max, state) =>
            state.balance > max.balance ? state : max, states[0],
          );
        }

        if (stateWithBiggestBalance && stateWithBiggestBalance.balance > 0n) {
          global = updateAccountStaking(global, accountId, {
            stakingId: stateWithBiggestBalance.id,
          });
        } else if (shouldUseNominators && stateById.nominators) {
          global = updateAccountStaking(global, accountId, {
            stakingId: stateById.nominators.id,
          });
        }
      }

      // Collect all new staking slugs for auto-pinning
      const newStakingSlugs = states
        .filter((state) => {
          const isNewStaking = !prevStakingIds.has(state.id);
          const isActive = getIsActiveStakingState(state);
          return isNewStaking && isActive;
        })
        .map((state) => `${STAKING_SLUG_PREFIX}${state.tokenSlug}`);
      const hasNewPins = newStakingSlugs.length > 0;

      if (hasNewPins) {
        const accountSettings = selectAccountSettings(global, accountId) || {};
        const { pinnedSlugs = [] } = accountSettings;

        const newPinnedSlugs = unique(newStakingSlugs.concat(pinnedSlugs));

        global = updateAccountSettings(global, accountId, {
          ...accountSettings,
          pinnedSlugs: newPinnedSlugs,
        });
      }

      setGlobal(global);
      break;
    }

    case 'updateTokens': {
      const { tokens, kind, removedSlugs } = update;
      global = updateTokens(global, tokens, kind, removedSlugs, true);
      setGlobal(global);
      actions.preloadTokenDetails();
      break;
    }

    case 'updateSwapTokens': {
      global = updateSwapTokens(global, update.tokens);
      setGlobal(global);

      break;
    }

    case 'updateCurrencyRates': {
      global = updateCurrencyRates(global, update.rates);
      setGlobal(global);
      break;
    }

    case 'updateNfts': {
      const { chain, accountId, collectionAddress, isFullLoading, streamedAddresses } = update;
      const nfts = buildCollectionByKey(update.nfts, 'address');
      const currentNfts = selectAccountState(global, accountId)?.nfts;
      const newOrderedAddresses = Object.keys(nfts);

      const shouldAppend = Boolean(collectionAddress) || Boolean(isFullLoading);

      let byAddress: Record<string, ApiNft>;
      let orderedAddresses: string[];

      if (streamedAddresses) {
        // Streaming complete - prune NFTs not seen during the session for this chain
        const streamed = new Set(streamedAddresses);
        const prunedByAddress = { ...currentNfts?.byAddress, ...nfts };
        for (const addr of Object.keys(prunedByAddress)) {
          if (prunedByAddress[addr].chain === chain && !streamed.has(addr)) {
            delete prunedByAddress[addr];
          }
        }
        byAddress = prunedByAddress;
        orderedAddresses = (currentNfts?.orderedAddresses ?? [])
          .filter((addr) => streamed.has(addr) || currentNfts?.byAddress?.[addr]?.chain !== chain);
      } else if (shouldAppend) {
        // Batch or collection loading - preserve existing entries (fresher websocket data), except the
        // verification verdict, which the incoming batch recomputes from the current trusted collection list.
        // An entry stored without metadata is replaced entirely, as the incoming batch may carry the metadata.
        byAddress = { ...nfts };
        for (const [address, existingNft] of Object.entries(currentNfts?.byAddress ?? {})) {
          const incomingNft = nfts[address];
          byAddress[address] = incomingNft && !existingNft.isMetadataMissing
            ? omitUndefined({ ...existingNft, isUnverified: incomingNft.isUnverified })
            : incomingNft ?? existingNft;
        }
        orderedAddresses = unique(
          ([] as string[]).concat(currentNfts?.orderedAddresses ?? [], newOrderedAddresses),
        );
      } else {
        // Non-streaming full update - new data takes priority
        byAddress = { ...currentNfts?.byAddress, ...nfts };
        orderedAddresses = unique(
          ([] as string[]).concat(newOrderedAddresses, currentNfts?.orderedAddresses ?? []),
        );
      }

      orderedAddresses = pinMwCardsFirst(orderedAddresses, byAddress);

      global = updateAccountState(global, accountId, {
        nfts: {
          ...currentNfts,
          byAddress,
          orderedAddresses,
          isLoadedByAddress: {
            ...currentNfts?.isLoadedByAddress,
            ...(shouldAppend && Boolean(collectionAddress) ? { [collectionAddress]: true } : {}),
          },
          collectionLoadedTimestamps: {
            ...currentNfts?.collectionLoadedTimestamps,
            ...(shouldAppend && Boolean(collectionAddress) ? { [collectionAddress]: Date.now() } : {}),
          },
          isFullLoadCompleteByChain: isFullLoading !== undefined ? {
            ...currentNfts?.isFullLoadCompleteByChain,
            [chain]: isFullLoading === false && streamedAddresses !== undefined,
          } : currentNfts?.isFullLoadCompleteByChain,
          isFullLoadingByChain: isFullLoading !== undefined ? {
            ...currentNfts?.isFullLoadingByChain,
            [chain]: isFullLoading,
          } : currentNfts?.isFullLoadingByChain,
        },
      });

      // Diff against persistent `ownedSet` so a card the user removed (via `clearCardBackgroundNft`)
      // isn't re-installed every polling round when it remains in the wallet
      const ownedSet = new Set(currentNfts?.ownedMwCardAddresses ?? []);
      const newCards = update.nfts.filter((nft) =>
        nft.collectionAddress === MW_CARDS_COLLECTION
        && !ownedSet.has(nft.address),
      );
      if (newCards.length) {
        pendingNewMwCardsByAccount.set(accountId, [
          ...(pendingNewMwCardsByAccount.get(accountId) ?? []),
          ...newCards,
        ]);
      }

      update.nfts.forEach((nft) => {
        if (nft.collectionAddress === MW_CARDS_COLLECTION) {
          global = updateAccountSettingsBackgroundNft(global, nft);
        }
      });

      const hasTelegramGifts = update.nfts.some((nft) => nft.isTelegramGift);
      if (hasTelegramGifts) {
        addTelegramGiftsTab(actions);
      }

      setGlobal(global);

      // On the round's final batch: rebuild `ownedSet` from current ownership, then auto-install
      // a new card if the user has none set
      if (streamedAddresses) {
        const candidates = pendingNewMwCardsByAccount.get(accountId);
        pendingNewMwCardsByAccount.delete(accountId);

        const byAddressNow = selectAccountState(getGlobal(), accountId)?.nfts?.byAddress;
        if (byAddressNow) {
          // Sync `ownedSet` BEFORE auto-install so subsequent rounds see the current ownership
          const currentMwAddresses = Object.values(byAddressNow)
            .filter((nft) => nft.collectionAddress === MW_CARDS_COLLECTION)
            .map((nft) => nft.address);
          global = updateAccountOwnedMwCards(getGlobal(), accountId, currentMwAddresses);
          setGlobal(global);
        }

        if (candidates?.length) {
          const settings = selectAccountSettings(getGlobal(), accountId);
          if (!settings?.cardBackgroundNft) {
            // Pick rarest = MIN by (metadata.mtwCardId ?? index) - earlier mints are typically rarer
            const rarest = candidates.reduce((acc, candidate) => (
              (candidate.metadata?.mtwCardId ?? candidate.index) < (acc.metadata?.mtwCardId ?? acc.index)
                ? candidate
                : acc
            ));
            actions.setCardBackgroundNft({ nft: rarest, accountId });
            actions.installAccentColorFromNft({ nft: rarest, accountId });
          }
        }
      }

      actions.checkCardNftOwnership({ accountId });
      break;
    }

    case 'nftSent': {
      const { accountId, nftAddress, newOwnerAddress } = update;
      const sentNft = selectAccountNftByAddress(global, accountId, nftAddress);
      if (sentNft) {
        global = applyOutgoingNftFromActivity(global, accountId, sentNft, newOwnerAddress);
      } else {
        // Fallback if NFT isn't in local state (e.g., startup race - socket event arrived before initial load)
        global = removeNft(global, accountId, nftAddress);
      }
      setGlobal(global);

      actions.checkCardNftOwnership({ accountId });
      break;
    }

    case 'nftReceived': {
      const { accountId, nft } = update;
      global = applyIncomingNftFromActivity(global, accountId, nft);
      setGlobal(global);

      if (nft.isTelegramGift) addTelegramGiftsTab(actions);
      actions.checkCardNftOwnership({ accountId });
      const settings = selectAccountSettings(global, accountId);
      if (nft.collectionAddress === MW_CARDS_COLLECTION && !settings?.cardBackgroundNft) {
        actions.setCardBackgroundNft({ nft, accountId });
        actions.installAccentColorFromNft({ nft, accountId });
      }
      break;
    }

    case 'nftChanged': {
      const { accountId, nft } = update;
      global = selectAccountNftByAddress(global, accountId, nft.address)
        ? replaceNft(global, accountId, nft)
        : applyIncomingNftFromActivity(global, accountId, nft);
      if (nft.collectionAddress === MW_CARDS_COLLECTION) {
        global = updateAccountSettingsBackgroundNft(global, nft);
      }
      setGlobal(global);
      if (nft.isTelegramGift) addTelegramGiftsTab(actions);
      break;
    }

    case 'nftPutUpForSale': {
      const { accountId, nftAddress, nft } = update;
      if (selectAccountNftByAddress(global, accountId, nftAddress)) {
        global = updateNft(global, accountId, nftAddress, {
          isOnSale: true,
        });
      } else if (nft) {
        // The outgoing activity of the listing transfer removes the NFT when it arrives before this update
        global = applyIncomingNftFromActivity(global, accountId, nft);
      }
      setGlobal(global);
      break;
    }

    case 'updateAccount': {
      const {
        accountId, chain, accountType, domain, address, isMultisig, derivation, version, mfa,
      } = update;
      const account = selectAccount(global, accountId);
      if (!account) {
        break;
      }

      if (accountType !== undefined) {
        global = updateAccount(global, accountId, { type: accountType });

        if (!chain) {
          setGlobal(global);
          break;
        }
      }

      if (!chain) {
        break;
      }

      if (!account.byChain[chain]) {
        if (!address) {
          break;
        }

        global = updateAccount(global, accountId, {
          byChain: {
            ...account.byChain,
            [chain]: {
              address,
              ...(domain ? { domain } : {}),
              ...(isMultisig ? { isMultisig: true } : {}),
              ...(derivation ? { derivation } : {}),
              ...(version ? { version } : {}),
            },
          },
        });
        setGlobal(global);
        break;
      }

      const chainUpdate: Partial<AccountChain> = {};
      if (address) {
        chainUpdate.address = address;
      }
      if (domain !== undefined) {
        chainUpdate.domain = domain || undefined;
      }
      if (isMultisig !== undefined) {
        chainUpdate.isMultisig = isMultisig || undefined;
      }
      if (derivation !== undefined) {
        chainUpdate.derivation = derivation;
      }
      if (version !== undefined) {
        chainUpdate.version = version;
      }
      if (mfa !== undefined) {
        chainUpdate.mfa = mfa || undefined;
      }
      global = updateAccountChain(global, accountId, chain, chainUpdate);
      setGlobal(global);
      break;
    }

    case 'updateConfig': {
      const {
        isLimited: isLimitedRegion,
        isCopyStorageEnabled,
        supportAccountsCount,
        countryCode,
        isAppUpdateRequired,
        swapVersion,
        seasonalTheme,
        allowedOnOffRampCurrencies,
      } = update;

      const normalizedRampCurrencies = normalizeAllowedOnOffRampCurrencies(allowedOnOffRampCurrencies);
      const previousRampCurrencies = global.restrictions.allowedOnOffRampCurrencies;

      global = updateRestrictions(global, {
        isLimitedRegion,
        isSwapDisabled: false,
        isOnRampDisabled: false,
        isOffRampDisabled: false,
        // The `restrictions` object is cached, so an excluded key will allow a stale value stored in an older build
        // to survive a shallow merge with a cached state
        isNftBuyingDisabled: false,
        isCopyStorageEnabled,
        supportAccountsCount,
        countryCode,
        // Keep the previous reference for an unchanged list so connected containers do not re-render on every poll
        allowedOnOffRampCurrencies: areDeepEqual(normalizedRampCurrencies, previousRampCurrencies)
          ? previousRampCurrencies
          : normalizedRampCurrencies,
      });
      global = {
        ...global,
        isAppUpdateRequired: IS_GRAM_WALLET ? undefined : isAppUpdateRequired,
        swapVersion: swapVersion ?? SWAP_API_VERSION,
        seasonalTheme,
      };
      setGlobal(global);
      break;
    }

    case 'updateWalletVersions': {
      actions.apiUpdateWalletVersions(update);
      break;
    }

    case 'openUrl': {
      void openUrl(update.url, { isExternal: update.isExternal, title: update.title, subtitle: update.subtitle });
      break;
    }

    case 'requestReconnectApi': {
      actions.initApi();
      break;
    }

    case 'incorrectTime': {
      if (!global.isIncorrectTimeNotificationReceived) {
        actions.showIncorrectTimeError();
      }
      break;
    }

    case 'updateVesting': {
      const { accountId, vestingInfo } = update;
      const unfreezeRequestedIds = selectVestingPartsReadyToUnfreeze(global, accountId);
      global = updateVestingInfo(global, accountId, vestingInfo);
      const newUnfreezeRequestedIds = selectVestingPartsReadyToUnfreeze(global, accountId);
      if (!areDeepEqual(unfreezeRequestedIds, newUnfreezeRequestedIds)) {
        global = updateVesting(global, accountId, { unfreezeRequestedIds: undefined });
      }
      setGlobal(global);
      break;
    }

    case 'updatingStatus': {
      const { kind, accountId, isUpdating } = update;
      const key = kind === 'balance' ? 'balanceUpdateStartedAt' : 'activitiesUpdateStartedAt';
      const accountState = selectAccountState(global, accountId);
      if (isUpdating && accountState?.[key]) break;

      global = updateAccountState(global, accountId, {
        [key]: isUpdating ? Date.now() : undefined,
      });

      // Set `isAppReady` when balance loading is complete
      if (!accountState?.isAppReady && kind === 'balance' && !isUpdating) {
        global = updateAccountState(global, accountId, { isAppReady: true });
      }

      setGlobal(global);
      break;
    }

    case 'removeAccounts': {
      const { accountIds } = update;
      const removed = new Set(accountIds);
      const wasCurrentRemoved = Boolean(global.currentAccountId && removed.has(global.currentAccountId));
      global = omitAccounts(global, accountIds);

      // Drop any push-notification references to the removed accounts (local only; twins were never subscribed).
      const { enabledAccounts } = global.pushNotifications;
      const nextEnabledAccounts = enabledAccounts.filter((id) => !removed.has(id));
      if (nextEnabledAccounts.length !== enabledAccounts.length) {
        global = {
          ...global,
          pushNotifications: { ...global.pushNotifications, enabledAccounts: nextEnabledAccounts },
        };
      }

      setGlobal(global);

      // `omitAccounts` clears `currentAccountId` when the active account is removed, but nothing downstream
      // self-heals from `currentAccountId === undefined` (`activateAccount` bails out). Re-select a survivor via
      // `switchAccount`, which also syncs `settings.isTestnet`.
      // Zero survivors is a full wipe: leave `currentAccountId` undefined. `orderedAccountIds` is a merge-only
      // hint that can retain ids of accounts removed in earlier sessions, so pick the first one still live.
      if (wasCurrentRemoved) {
        const survivorId = global.settings.orderedAccountIds?.find((id) => id in global.byAccountId)
          ?? Object.keys(global.byAccountId)[0];
        if (survivorId) {
          actions.switchAccount({ accountId: survivorId, newNetwork: parseAccountId(survivorId).network });
        }
      }
      break;
    }

    case 'legacyCoreMigrationReady': {
      const pending = pendingLegacySignIn;
      pendingLegacySignIn = undefined;
      const account = selectAccount(global, update.accountId);
      if (pending?.accountId === update.accountId && global.currentAccountId === update.accountId
        && global.appState === AppState.Auth && global.auth === pending.auth
        && global.isLegacyCoreMigrationCompleted && account?.type === 'mnemonic'
        && account.byChain.ton?.address === pending.address) {
        actions.afterSignIn();
      }
      break;
    }

    case 'migrateLegacyCoreApplication': {
      if (global.isLegacyCoreMigrationCompleted) {
        break;
      }

      const hasConflict = update.accounts.some(({ accountId, address }) => {
        const existing = selectAccount(global, accountId);
        return existing && (existing.type !== 'mnemonic' || existing.byChain.ton?.address !== address);
      });
      if (hasConflict) {
        pendingLegacySignIn = undefined;
        actions.showError({ error: 'Migration error' });
        break;
      }

      for (const { accountId, address } of update.accounts) {
        if (!selectAccount(global, accountId)) {
          global = createAccount({
            global,
            accountId,
            type: 'mnemonic',
            byChain: { ton: { address } },
            network: parseAccountId(accountId).network,
          });
        }
      }
      const hasSelectedAccount = update.accounts.some(({ accountId }) => accountId === global.currentAccountId);
      const accountId = hasSelectedAccount ? global.currentAccountId! : update.currentAccountId;
      if (!hasSelectedAccount && global.appState === AppState.Auth) {
        pendingLegacySignIn = {
          accountId, address: selectAccount(global, accountId)!.byChain.ton!.address, auth: global.auth,
        };
      }
      global = updateCurrentAccountId(global, accountId);
      global = updateSettings(global, { isTestnet: parseAccountId(accountId).network === 'testnet' });
      setGlobal({ ...global, isLegacyCoreMigrationCompleted: true });
      try {
        if (!persistCache()) {
          setGlobal({ ...getGlobal(), isLegacyCoreMigrationCompleted: undefined });
          break;
        }
      } catch {
        setGlobal({ ...getGlobal(), isLegacyCoreMigrationCompleted: undefined });
        actions.showError({ error: 'Migration error' });
        break;
      }
      break;
    }

    case 'updateAccountConfig': {
      const { accountConfig, accountId } = update;
      global = updateAccountState(global, accountId, { config: accountConfig });
      setGlobal(global);
      break;
    }

    case 'updateAccountBanners': {
      const { accountId, homeBanners, stakingBanners } = update;
      global = updateAccountBanners(global, accountId, homeBanners, stakingBanners);
      setGlobal(global);
      break;
    }

    case 'updateAccountDomainData': {
      const {
        accountId,
        expirationByAddress,
        linkedAddressByAddress,
        nfts: updatedNfts,
      } = update;
      const nfts = selectAccountState(global, accountId)?.nfts || { byAddress: {} };

      global = updateAccountState(global, accountId, {
        nfts: {
          ...nfts,
          dnsExpiration: expirationByAddress,
          linkedAddressByAddress,
        },
      });
      global = addUnorderedNfts(global, accountId, updatedNfts);
      setGlobal(global);
      break;
    }
  }
});

function addTelegramGiftsTab(actions: Pick<ReturnType<typeof getActions>, 'addCollectionTab'>) {
  actions.addCollectionTab({
    collection: {
      address: TELEGRAM_GIFTS_SUPER_COLLECTION,
      chain: 'ton',
    },
    isAuto: true,
  });
}
